/**
 * How a screen gets hold of the API. One context, no singletons.
 *
 * A module-level `const api = createApplicationApi()` would be shorter and would also make the
 * feature untestable and impossible to point at a different scenario, so the app root creates the
 * pair and passes them in. That is also what makes the seam in `api/create.ts` a real seam: the
 * choice is made in exactly one place and everything else receives the result.
 *
 * ★ AND ONE STEP OF THE FLOW LIVES HERE, BECAUSE THIS IS THE ONE PLACE BOTH ENDS OF IT CAN REACH.
 * G30 (design 22 §1.4): when the server refuses an application from an account with no way to
 * sign back in, the actions hook hears it (`useApplicationActions`) and the whole page has to give
 * way to the link step (../screens/SignInMethodStep.tsx), then come back. The hook is under this
 * provider and the page is its child, so the provider holds the one flag: `requireSignInMethod()`
 * sets it, and while it is set the step is drawn INSTEAD of the children - which come back, and
 * re-read everything, when the step is done. `routes/apply/index.tsx` mounts this provider and did
 * not have to change; a spec that mounts the screens bare never meets the refusal, so it never
 * draws the step either.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ApplicationApi, QuarantineUploader } from '../api/types';
import { SignInMethodStep } from '../screens/SignInMethodStep';

interface ApplicationApiContextValue {
  readonly api: ApplicationApi;
  readonly uploader: QuarantineUploader;
  /** G30: draw the link step in place of the page, until it is done. See the header. */
  readonly requireSignInMethod: () => void;
}

const ApplicationApiContext = createContext<ApplicationApiContextValue | undefined>(undefined);

export interface ApplicationApiProviderProps {
  readonly api: ApplicationApi;
  readonly uploader: QuarantineUploader;
  readonly children: ReactNode;
}

export function ApplicationApiProvider({
  api,
  uploader,
  children,
}: ApplicationApiProviderProps): ReactNode {
  const [signInMethodAsked, setSignInMethodAsked] = useState(false);
  const requireSignInMethod = useCallback(() => {
    setSignInMethodAsked(true);
  }, []);
  const backToApplication = useCallback(() => {
    setSignInMethodAsked(false);
  }, []);
  const value = useMemo(
    () => ({ api, uploader, requireSignInMethod }),
    [api, uploader, requireSignInMethod],
  );
  return (
    <ApplicationApiContext value={value}>
      {signInMethodAsked ? <SignInMethodStep onDone={backToApplication} /> : children}
    </ApplicationApiContext>
  );
}

export function useApplicationApi(): ApplicationApi {
  const value = useContext(ApplicationApiContext);
  if (value === undefined) {
    throw new Error(
      'useApplicationApi was called outside <ApplicationApiProvider>. The application screens ' +
        'are mounted by web/authors/src/routes/apply, which supplies it.',
    );
  }
  return value.api;
}

export function useQuarantineUploader(): QuarantineUploader {
  const value = useContext(ApplicationApiContext);
  if (value === undefined) {
    throw new Error('useQuarantineUploader was called outside <ApplicationApiProvider>.');
  }
  return value.uploader;
}

/** G30's way to the link step, for the one hook that hears the refusal. */
export function useRequireSignInMethod(): () => void {
  const value = useContext(ApplicationApiContext);
  if (value === undefined) {
    throw new Error('useRequireSignInMethod was called outside <ApplicationApiProvider>.');
  }
  return value.requireSignInMethod;
}
